import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import './SubscriptionsPage.css';

/**
 * Subscriptions & Modules management page.
 */
export const SubscriptionsPage = () => {
  const { tenant, entitlements, hasFeature, refreshEntitlements } = useEntitlements();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [moduleEntitlements, setModuleEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processingCheckout, setProcessingCheckout] = useState(false);

  useEffect(() => {
    fetchData();
    handleRedirectParams();
  }, []);

  /**
   * Handle success/cancel redirects from Stripe Checkout.
   */
  const handleRedirectParams = () => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('success') === 'true') {
      setSuccessMessage('Payment successful! Your subscription is being activated.');
      // Refresh data to show new subscription
      setTimeout(() => {
        fetchData();
      }, 2000);

      // Clean up URL
      window.history.replaceState({}, '', '/settings/subscriptions');
    }

    if (params.get('canceled') === 'true') {
      setError('Payment was canceled. Please try again if you wish to subscribe.');

      // Clean up URL
      window.history.replaceState({}, '', '/settings/subscriptions');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription
      if (tenant) {
        const subResponse = await api.get('/v1/subscriptions/', {
          params: { tenant: tenant.id },
        });
        if (subResponse.data.results && subResponse.data.results.length > 0) {
          setSubscription(subResponse.data.results[0]);
        }
      }

      // Fetch plans
      const plansResponse = await api.get('/v1/subscription-plans/');
      const plansData = plansResponse.data.results || plansResponse.data || [];
      console.log('Plans response:', plansResponse.data);
      console.log('Plans data:', plansData);
      setPlans(Array.isArray(plansData) ? plansData : []);

      // Fetch module entitlements
      if (tenant) {
        const entitlementsResponse = await api.get('/v1/entitlements/', {
          params: { tenant: tenant.id },
        });
        setModuleEntitlements(entitlementsResponse.data.results || entitlementsResponse.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (entitlement) => {
    try {
      setError(null);
      setSuccessMessage(null);

      if (entitlement.is_enabled) {
        // Disable module
        await api.post(`/v1/entitlements/${entitlement.id}/disable/`);
        setSuccessMessage(`${entitlement.module} module disabled successfully`);
      } else {
        // Enable module
        await api.post(`/v1/entitlements/${entitlement.id}/enable/`);
        setSuccessMessage(`${entitlement.module} module enabled successfully`);
      }

      // Refresh entitlements and data
      await refreshEntitlements();
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update module');
    }
  };

  const handleEnableTax = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      // Find or create tax entitlement
      let taxEntitlement = moduleEntitlements.find(
        (e) => e.module === 'tax'
      );

      if (!taxEntitlement && tenant) {
        // Create tax entitlement
        const createResponse = await api.post('/v1/entitlements/', {
          tenant: tenant.id,
          module: 'tax',
          is_enabled: false,
        });
        taxEntitlement = createResponse.data;
      }

      if (taxEntitlement) {
        await api.post(`/v1/entitlements/${taxEntitlement.id}/enable/`);
        setSuccessMessage('Portalr Tax enabled successfully!');
        await refreshEntitlements();
        await fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enable Tax module');
    }
  };

  /**
   * Handle plan selection - create Stripe Checkout Session.
   */
  const handleSelectPlan = async (plan) => {
    try {
      setError(null);
      setSuccessMessage(null);
      setProcessingCheckout(true);

      console.log('Creating checkout session for plan:', plan.slug, 'billing cycle:', billingCycle);

      // Create Stripe Checkout Session
      const response = await api.post('/v1/stripe/create-checkout-session', {
        plan_slug: plan.slug,
        billing_cycle: billingCycle,
      });

      console.log('Checkout session created:', response.data);

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned from server');
      }

    } catch (err) {
      console.error('Checkout session error:', err);
      setProcessingCheckout(false);

      // Show detailed error message
      const errorMessage = err.response?.data?.error
        || err.response?.data?.detail
        || err.message
        || 'Failed to create checkout session. Please try again.';

      setError(errorMessage);
    }
  };

  /**
   * Open Stripe Customer Portal for subscription management.
   */
  const handleManageSubscription = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      // Create Customer Portal Session
      const response = await api.post('/v1/stripe/create-portal-session');

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open subscription management portal.');
    }
  };

  if (loading) {
    return (
      <div className="subscriptions-loading">
        <div className="loading-spinner">Loading subscriptions...</div>
      </div>
    );
  }

  const taxEntitlement = moduleEntitlements.find((e) => e.module === 'tax');
  const taxEnabled = hasFeature('enable_tax');

  // Separate Core plans from Tax plans
  const corePlans = plans.filter((plan) => !plan.metadata?.module || plan.metadata.module !== 'tax');
  const taxPlans = plans.filter((plan) => plan.metadata?.module === 'tax');

  return (
    <div className="subscriptions-page">
      <div className="subscriptions-header">
        <h1>Subscriptions & Modules</h1>
        <p>Manage your subscription plan and enabled modules</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      {/* Current Subscription */}
      <section className="subscriptions-section">
        <h2>Current Plan</h2>
        {subscription ? (
          <div className="subscription-card active">
            <div className="subscription-info">
              <h3>{subscription.plan_name}</h3>
              <p>Status: <span className={`status-badge status-${subscription.status}`}>
                {subscription.status?.charAt(0).toUpperCase() + subscription.status?.slice(1)}
              </span></p>
              <p>Billing Cycle: <strong>{subscription.billing_cycle?.charAt(0).toUpperCase() + subscription.billing_cycle?.slice(1)}</strong></p>
              {subscription.current_period_end && (
                <p>Next Billing: {new Date(subscription.current_period_end).toLocaleDateString()}</p>
              )}
              {subscription.cancel_at_period_end && (
                <p className="cancel-notice">⚠️ Subscription will cancel at period end</p>
              )}
            </div>
            <div className="subscription-actions">
              <button
                className="btn btn-secondary"
                onClick={handleManageSubscription}
              >
                Manage Subscription
              </button>
            </div>
          </div>
        ) : (
          <div className="subscription-card empty">
            <p>No active subscription. Select a plan below to get started.</p>
          </div>
        )}
      </section>

      {/* Core Plans */}
      <section className="subscriptions-section">
        <div className="section-header">
          <h2>Portalr Core Plans</h2>
          <div className="billing-cycle-toggle">
            <button
              className={`toggle-option ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`toggle-option ${billingCycle === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly <span className="savings-badge">Save 17%</span>
            </button>
          </div>
        </div>
        {corePlans.length === 0 ? (
          <div className="subscription-card empty">
            <p>No plans available</p>
          </div>
        ) : (
          <div className="plans-grid">
            {corePlans.map((plan) => {
            const planIcon = plan.slug === 'starter' ? '🟢' : plan.slug === 'professional' ? '🔵' : '🟣';
            const metadata = plan.metadata || {};
            const bestFor = metadata.best_for || [];
            const idealOutcome = metadata.ideal_outcome || '';
            const isCurrentPlan = subscription?.plan === plan.id;
            const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
            const priceLabel = billingCycle === 'monthly' ? '/month' : '/year';

            return (
              <div key={plan.id} className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}>
                {isCurrentPlan && <div className="current-plan-badge">Current Plan</div>}
                <div className="plan-icon">{planIcon}</div>
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="plan-amount">${price}</span>
                  <span className="plan-period">{priceLabel}</span>
                </div>
                <p className="plan-description">{plan.description}</p>

                {bestFor.length > 0 && (
                  <div className="plan-best-for">
                    <h4>Best for</h4>
                    <ul>
                      {bestFor.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="plan-features-section">
                  <h4>What it delivers</h4>
                  <ul className="plan-features">
                    {plan.features && plan.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                {idealOutcome && (
                  <div className="plan-outcome">
                    <h4>Ideal outcome</h4>
                    <p>{idealOutcome}</p>
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  onClick={() => handleSelectPlan(plan)}
                  disabled={processingCheckout || isCurrentPlan}
                >
                  {processingCheckout ? 'Processing...' : isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                </button>
              </div>
            );
            })}
          </div>
        )}
      </section>

      {/* Tax Module Plans - Only show if Tax is enabled */}
      {taxEnabled && taxPlans.length > 0 && (
        <section className="subscriptions-section">
          <h2>Portalr Tax Plans</h2>
          <div className="plans-grid">
            {taxPlans.map((plan) => {
              const planIcon = plan.slug === 'tax-starter' ? '🟢' : plan.slug === 'tax-professional' ? '🔵' : '🟣';
              const metadata = plan.metadata || {};
              const bestFor = metadata.best_for || [];
              const idealOutcome = metadata.ideal_outcome || '';
              const isCurrentPlan = subscription?.plan === plan.id;
              const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
              const priceLabel = billingCycle === 'monthly' ? '/month' : '/year';

              return (
                <div key={plan.id} className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}>
                  {isCurrentPlan && <div className="current-plan-badge">Current Plan</div>}
                  <div className="plan-icon">{planIcon}</div>
                  <h3>{plan.name}</h3>
                  <div className="plan-price">
                    <span className="plan-amount">${price}</span>
                    <span className="plan-period">{priceLabel}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>

                  {bestFor.length > 0 && (
                    <div className="plan-best-for">
                      <h4>Best for</h4>
                      <ul>
                        {bestFor.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="plan-features-section">
                    <h4>What it delivers</h4>
                    <ul className="plan-features">
                      {plan.features && plan.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  {idealOutcome && (
                    <div className="plan-outcome">
                      <h4>Ideal outcome</h4>
                      <p>{idealOutcome}</p>
                    </div>
                  )}

                  <button
                    className="btn btn-primary"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={processingCheckout || isCurrentPlan}
                  >
                    {processingCheckout ? 'Processing...' : isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Modules */}
      <section className="subscriptions-section">
        <h2>Modules</h2>
        <div className="modules-list">
          {/* Tax Module Toggle */}
          {taxEntitlement ? (
            <div className="module-card">
              <div className="module-info">
                <h3>Portalr Tax</h3>
                <p className="module-status">
                  Status: <span className={`status-badge status-${taxEntitlement.is_enabled ? 'enabled' : 'disabled'}`}>
                    {taxEntitlement.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </p>
                <p>Tax management and compliance features</p>
              </div>
              <div className="module-actions">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={taxEntitlement.is_enabled}
                    onChange={() => handleToggleModule(taxEntitlement)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          ) : (
            <div className="module-card module-cta">
              <div className="module-info">
                <h3>Portalr Tax</h3>
                <p>Enable tax management and compliance features to access Tax module plans</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleEnableTax}
              >
                Enable Portalr Tax
              </button>
            </div>
          )}

          {/* Other Module Entitlements */}
          {moduleEntitlements.filter((e) => e.module !== 'tax').map((entitlement) => (
            <div key={entitlement.id} className="module-card">
              <div className="module-info">
                <h3>{entitlement.module.charAt(0).toUpperCase() + entitlement.module.slice(1)}</h3>
                <p className="module-status">
                  Status: <span className={`status-badge status-${entitlement.is_enabled ? 'enabled' : 'disabled'}`}>
                    {entitlement.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </p>
              </div>
              <div className="module-actions">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={entitlement.is_enabled}
                    onChange={() => handleToggleModule(entitlement)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

